import React from 'react';
import { TrendingUp, TrendingDown, DollarSign, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface FinanceCardProps {
  revenue: number;
  expenses: number;
  profit: number;
  revenueChange: number;
  expensesChange: number;
  onViewReport?: () => void;
}

export function FinanceCard({
  revenue = 0,
  expenses = 0,
  profit = 0,
  revenueChange = 0,
  expensesChange = 0,
  onViewReport = () => {},
}: Partial<FinanceCardProps>) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value > 0 ? '+' : ''}${value}%`;
  };

  return (
    <Card className="border-startup-teal/20 hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <CardTitle className="text-startup-navy flex items-center">
          <DollarSign className="w-5 h-5 mr-2 text-startup-teal" />
          Finance Overview
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Revenue */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-startup-gray">Revenue</span>
            <div className={`flex items-center text-sm ${revenueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {revenueChange >= 0 ? (
                <ArrowUpRight className="w-4 h-4 mr-1" />
              ) : (
                <ArrowDownRight className="w-4 h-4 mr-1" />
              )}
              {formatPercentage(revenueChange)}
            </div>
          </div>
          <div className="text-2xl font-semibold">{formatCurrency(revenue)}</div>
        </div>

        {/* Expenses */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-startup-gray">Expenses</span>
            <div className={`flex items-center text-sm ${expensesChange <= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {expensesChange <= 0 ? (
                <ArrowDownRight className="w-4 h-4 mr-1" />
              ) : (
                <ArrowUpRight className="w-4 h-4 mr-1" />
              )}
              {formatPercentage(Math.abs(expensesChange))}
            </div>
          </div>
          <div className="text-2xl font-semibold">{formatCurrency(expenses)}</div>
        </div>

        {/* Profit/Loss */}
        <div className="pt-3 border-t border-gray-100">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-startup-gray">Net Profit</span>
            <span className={`text-sm font-medium ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {profit >= 0 ? 'Profit' : 'Loss'}
            </span>
          </div>
          <div className={`text-2xl font-bold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(profit)}
          </div>
        </div>

        <div className="pt-2">
          <button 
            onClick={onViewReport}
            className="text-sm text-startup-teal hover:underline flex items-center focus:outline-none"
          >
            View detailed report <ArrowUpRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
